import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { MATIERE_TEST_IDS } from '@/components/admin/sessions/contracts';
import { fetchExperienceMatter, fetchExperiencesForMatter } from '@/lib/matter-api';
import { EMPTY_SELECT_VALUE, fromSelectValue, toSelectValue } from '@/lib/select-value';
import {
  objectiveStatusLabel,
  objectiveStatusTone,
  objectiveTone,
  sessionDate,
} from '@/lib/session-labels';
import { matterSearchSentence } from '@/lib/session-search';
import { cn } from '@/lib/utils';
import {
  type MatterExtract,
  type MatterObjective,
  type ObjectiveStatus,
  conversationUrl,
} from '@matter_extractor/shared';
import { Alert, Select, Spinner } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * L'ONGLET MATIÈRE (refonte modulaire §12.4) - « par expérience, la liste des
 * extraits par objectif à travers toutes ses sessions, filtrable par statut
 * d'objectif et par version d'expérience ».
 *
 * ## An autonomous component, and what that costs it
 *
 * It mounts its OWN queries, the régime §10.1 and §6 already set for the persona
 * and criterion forms: the tab that will host it hands it at most an experience
 * id, and everything else - the library, the matter, the two filters - is this
 * file's business. So it can be dropped in place of a seam in one line, and the
 * route that hosts it learns nothing about §12.4.
 *
 * The selection is held HERE rather than lifted, and `onExperienceChange` is the
 * only thing that leaves: a route that wants the choice in its address mirrors
 * it, and one that does not passes nothing at all. The alternative - a fully
 * controlled panel - would have made the one-line mount impossible.
 *
 * ## The deep link is a plain anchor, deliberately
 *
 * « L'extrait renvoie à la session qui l'a produit » lands on the conversation
 * of §12.3, pinned on the turn (§15). It is written as an `<a href>` and not a
 * typed `<Link>` because the three Sessions screens are landed by a lot beside
 * this one: a typed route that does not exist yet is a type error, and a panel
 * that cannot compile until another branch merges is a panel nobody can land.
 * The address comes from `conversationUrl` of `shared` (issue #94), which is
 * where the ONE shift of that address lives: `?tour=` is the number a human
 * reads, 1-based, and an extract carries the turn's 0-based `index`. This
 * anchor used to spell the address itself, without the shift, and pinned the
 * turn before the one that said the extract.
 *
 * ## An invalidated extract is struck through, never hidden
 *
 * §8.4 keeps it in the journal marked, and so does this: `line-through` plus the
 * word « invalidé », because a strike-through alone is a meaning carried by a
 * shape and read by nobody using a screen reader. Filtering it out would hide
 * what a person actually said, which is the opposite of what a corpus is for.
 */

/** The three statuses of §7.3, in the order the implicit logic reaches them. */
const STATUSES: readonly ObjectiveStatus[] = ['succeeded', 'failed', 'empty'];

export interface MatierePanelProps {
  /**
   * The experience the panel opens on, when the address already names one.
   *
   * Absent or null: the panel opens on the first experience of the library,
   * because a tab that asks a question before showing anything is a tab whose
   * first screen is empty.
   */
  readonly experienceId?: string | null;
  /**
   * Told whenever the operator picks another, so a route can mirror the choice
   * into its address. Optional: the panel works without one.
   */
  readonly onExperienceChange?: ((experienceId: string) => void) | undefined;
  /**
   * The search of the bar of Sessions (recherche-sessions.md §7, #213), when it is armed: the
   * sessions whose extracts are read are the ones it retains, and a sentence above the
   * objectives says so. It restricts SESSIONS, never the extracts by their text. Absent or null,
   * the panel is the one of before the search.
   */
  readonly q?: string | null | undefined;
}

export function MatierePanel({ experienceId, onExperienceChange, q = null }: MatierePanelProps) {
  const [picked, setPicked] = useState<string | null>(experienceId ?? null);
  const [status, setStatus] = useState<ObjectiveStatus | null>(null);
  const [versionId, setVersionId] = useState<string | null>(null);

  /*
    A key of its own rather than the `['experiences', email]` the two libraries
    share. `packages/frontend/CLAUDE.md` names the trap: two components on one
    key have to agree on the FORM with nothing checking it, and this panel is
    mounted by a lot that does not own either library. One extra read on mount,
    against a 30 s `staleTime` - and no coupling at all.
  */
  const library = useQuery({
    queryKey: ['matiere', 'experiences'],
    queryFn: fetchExperiencesForMatter,
  });

  const experiences = library.data?.experiences ?? [];
  // DERIVED and not synchronised: the first experience of the library is the
  // default until somebody picks another, and an effect that wrote it into
  // state would be a render that fights the one before it.
  const selected = picked ?? experiences[0]?.id ?? null;

  const matter = useQuery({
    queryKey: ['matiere', selected, status, versionId, q],
    queryFn: () =>
      fetchExperienceMatter(selected ?? '', {
        ...(status === null ? {} : { status }),
        ...(versionId === null ? {} : { experienceVersionId: versionId }),
        ...(q === null ? {} : { q }),
      }),
    enabled: selected !== null,
  });

  function pickExperience(id: string): void {
    setPicked(id);
    // The version belongs to the experience that was being read: keeping it
    // would filter the NEW experience on a version it never had, and answer an
    // empty page for a reason nothing on screen explains.
    setVersionId(null);
    onExperienceChange?.(id);
  }

  const versions = matter.data?.versions ?? [];
  const objectives = matter.data?.objectives ?? [];

  return (
    <section data-testid={MATIERE_TEST_IDS.panel} className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-4">
        {/*
          The package `Select` relays no `data-testid`: each mark is on the wrapper,
          which also carries the column's width. The empty choice (« Tous »,
          « Toutes ») crosses the Radix select as the sentinel of `select-value`.
        */}
        <div data-testid={MATIERE_TEST_IDS.experience} className="w-72 max-w-full">
          <Select
            label="Expérience"
            placeholder="Aucune expérience"
            options={experiences.map((experience) => ({
              value: experience.id,
              label: experience.name,
            }))}
            value={selected ?? ''}
            disabled={experiences.length === 0}
            onValueChange={pickExperience}
          />
        </div>

        <div data-testid={MATIERE_TEST_IDS.status} className="w-45 max-w-full">
          <Select
            label="Statut de l’objectif"
            options={[
              { value: EMPTY_SELECT_VALUE, label: 'Tous' },
              ...STATUSES.map((one) => ({ value: one, label: objectiveStatusLabel(one) })),
            ]}
            value={toSelectValue(status ?? '')}
            onValueChange={(value) => {
              const picked = fromSelectValue(value);
              setStatus(picked === '' ? null : (picked as ObjectiveStatus));
            }}
          />
        </div>

        <div data-testid={MATIERE_TEST_IDS.version} className="w-45 max-w-full">
          <Select
            label="Version"
            options={[
              { value: EMPTY_SELECT_VALUE, label: 'Toutes' },
              ...versions.map((version) => ({
                value: version.id,
                label: version.number === null ? 'version illisible' : `v${version.number}`,
              })),
            ]}
            value={toSelectValue(versionId ?? '')}
            onValueChange={(value) => {
              const picked = fromSelectValue(value);
              setVersionId(picked === '' ? null : picked);
            }}
          />
        </div>
      </div>

      {q === null || matter.data?.search === undefined ? null : (
        <p
          data-testid={MATIERE_TEST_IDS.searchNote}
          data-matched={matter.data.search.matchedSessions}
          data-total={matter.data.search.totalSessions}
          className="text-gray-700 text-sm"
        >
          {matterSearchSentence(q, matter.data.search)}
        </p>
      )}

      <MatiereBody
        searchedEmpty={q !== null && matter.data?.search?.matchedSessions === 0}
        loading={library.isPending || (matter.isPending && selected !== null)}
        failed={library.isError || matter.isError}
        hasExperience={selected !== null}
        objectives={objectives}
      />
    </section>
  );
}

interface MatiereBodyProps {
  readonly loading: boolean;
  readonly failed: boolean;
  readonly hasExperience: boolean;
  readonly objectives: readonly MatterObjective[];
  /** The search retains no session: its sentence above already says there is no extract. */
  readonly searchedEmpty: boolean;
}

/**
 * The four states of the body, in the order they exclude each other.
 *
 * Its own component so the panel above reads as « the controls, then the
 * matter »: the branching is the only thing here, and it is four lines.
 */
function MatiereBody({
  loading,
  failed,
  hasExperience,
  objectives,
  searchedEmpty,
}: MatiereBodyProps) {
  if (failed) {
    return <Alert tone="danger">La matière n’a pas pu être lue.</Alert>;
  }
  if (loading) return <Spinner label="Lecture de la matière" />;
  if (!hasExperience) {
    return (
      <p data-testid={MATIERE_TEST_IDS.empty} className="text-gray-600 text-small">
        Aucune expérience à lire.
      </p>
    );
  }
  if (objectives.length === 0) {
    // « Aucun extrait : aucune session ... » is said once, above, by the search's own sentence.
    if (searchedEmpty) return null;
    return (
      <p data-testid={MATIERE_TEST_IDS.empty} className="text-gray-600 text-small">
        Aucun extrait pour ces filtres.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-6">
      {objectives.map((objective) => (
        <ObjectiveBlock key={objective.objectiveId} objective={objective} />
      ))}
    </div>
  );
}

interface ObjectiveBlockProps {
  readonly objective: MatterObjective;
}

/**
 * One objective, with its step and its extracts.
 *
 * The step line says where the objective lived in the frozen protocol; when
 * nothing readable carries it any more it SAYS so, rather than dropping a line
 * and leaving the reader to wonder why this one is shorter.
 */
function ObjectiveBlock({ objective }: ObjectiveBlockProps) {
  return (
    <article
      data-testid={MATIERE_TEST_IDS.objective}
      data-objective-id={objective.objectiveId}
      className="rounded-md border border-gray-200 bg-white p-4"
    >
      <header className="flex flex-wrap items-baseline gap-2">
        <h3 className="font-semibold text-body text-bleu-nuit">{objective.name}</h3>
        <StatusBadge tone={objective.mandatory ? 'accent' : 'neutral'}>
          {objective.mandatory ? 'obligatoire' : 'optionnel'}
        </StatusBadge>
      </header>
      <p className="mt-1 text-gray-600 text-caption">
        {objective.step === null
          ? 'Aucune version lisible ne porte plus cet objectif'
          : `Étape ${objective.step.index} · ${objective.step.name}`}
        {' · '}
        {objective.extracts.length === 1 ? '1 extrait' : `${objective.extracts.length} extraits`}
      </p>
      <ul className="mt-3 flex flex-col gap-2.5">
        {objective.extracts.map((extract, at) => (
          <ExtractLine
            // The same words can legitimately appear twice under one objective -
            // invalidated on the turn that lost them, live on the turn the
            // person said them again (§8.4) - so the position is what tells two
            // lines apart, and the turn alone would not.
            key={`${extract.session.id}-${extract.turn.id}-${at}`}
            extract={extract}
          />
        ))}
      </ul>
    </article>
  );
}

interface ExtractLineProps {
  readonly extract: MatterExtract;
}

function ExtractLine({ extract }: ExtractLineProps) {
  const conversation = conversationUrl(extract.session.id, extract.turn.index);
  // A réussi validated at the passable bar (#112) badges yellow, filed under réussi.
  const tone = objectiveTone({
    status: extract.objectiveStatus,
    passableOnly: extract.objectivePassableOnly,
  });
  return (
    <li
      data-testid={MATIERE_TEST_IDS.extract}
      data-invalidated={extract.invalidated ? 'true' : undefined}
      data-session-id={extract.session.id}
      className="border-gray-100 border-l-2 pl-3"
    >
      <blockquote
        className={cn(
          'text-gray-800 text-small',
          // Struck through AND worded below: a meaning carried by a shape alone
          // is a meaning a screen reader never says.
          extract.invalidated && 'text-gray-500 line-through',
        )}
      >
        {`« ${extract.text} »`}
      </blockquote>
      <p className="mt-1 flex flex-wrap items-center gap-2 text-gray-600 text-caption">
        <a
          href={conversation}
          className="font-semibold text-bleu-nuit underline underline-offset-2"
        >
          {`${sessionDate(extract.session.startedAt)} · tour ${extract.turn.index + 1}`}
        </a>
        <StatusBadge tone={objectiveStatusTone(tone)}>{objectiveStatusLabel(tone)}</StatusBadge>
        {extract.invalidated ? <StatusBadge tone="warning">invalidé</StatusBadge> : null}
      </p>
    </li>
  );
}
